import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState, AppDispatch } from '../../redux/store';
import { fetchMarketingCampaignsRequest, setSelectedCampaign } from '../../redux/slices/marketingCampaignSlice';
import CampaignDetailModal from './CampaignDetailModal';
import LoadingState from '../common/LoadingState';
import type { ScheduledCampaign } from './types';
import { MdFacebook } from 'react-icons/md';
import { FaInstagram } from 'react-icons/fa';

type ViewMode = 'month' | 'week' | 'day';

const MarketingCalendar: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { campaigns, loading, selectedCampaign } = useSelector(
    (state: RootState) => state.marketingCampaign
  );

  const [viewMode, setViewMode] = useState<ViewMode>('month');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [clickPosition, setClickPosition] = useState<{ x: number; y: number } | null>(null);
  const [expandedCells, setExpandedCells] = useState<Set<string>>(new Set());
  const [showViewDropdown, setShowViewDropdown] = useState(false);

  useEffect(() => {
    dispatch(fetchMarketingCampaignsRequest('4bebbb8a-a2bf-4d53-b287-842c3c5be847'));
  }, [dispatch]);

  const handleEventClick = (campaign: ScheduledCampaign, event: React.MouseEvent) => {
    dispatch(setSelectedCampaign(campaign));
    setClickPosition({ x: event.clientX, y: event.clientY });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    dispatch(setSelectedCampaign(null));
    setClickPosition(null);
  };

  const parseSocialMedia = (socialMedia: string): string[] => {
    try {
      if (socialMedia.startsWith('[')) {
        const parsed = JSON.parse(socialMedia);
        return Array.isArray(parsed) ? parsed : [socialMedia];
      }
      return socialMedia.split(',').map(p => p.trim());
    } catch {
      return socialMedia.split(',').map(p => p.trim());
    }
  };

  const getSocialMediaIcon = (platform: string) => {
    switch (platform.toUpperCase()) {
      case 'FACEBOOK':
        return <MdFacebook className="text-blue-500" size={12} />;
      case 'INSTAGRAM':
        return <FaInstagram className="text-pink-500" size={12} />;
      default:
        return null;
    }
  };

  const formatDateForInput = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const groupCampaigns = (campaigns: ScheduledCampaign[]) => {
    const groupMap = new Map<string, ScheduledCampaign & { groupedCampaigns?: any[] }>();

    campaigns.forEach(campaign => {
      const flyerId = campaign.flyer_info?.id;
      const ideaTitle = campaign.generated_content?.idea_title;
      const groupKey = `${flyerId || 'no-flyer'}_${ideaTitle || campaign.id}`;

      const existing = groupMap.get(groupKey);

      if (existing) {
        const campaignPlatforms = parseSocialMedia(campaign.social_media);
        campaignPlatforms.forEach(platform => {
          if (!existing.groupedCampaigns) {
            existing.groupedCampaigns = [];
          }
          existing.groupedCampaigns.push({
            id: campaign.id,
            platform: platform,
            generated_content_id: campaign.generated_content_id
          });
        });

        const existingPlatforms = parseSocialMedia(existing.social_media);
        const newPlatforms = parseSocialMedia(campaign.social_media);
        const combinedPlatforms = [...new Set([...existingPlatforms, ...newPlatforms])];
        existing.social_media = JSON.stringify(combinedPlatforms);

        if (campaign.start_date < existing.start_date) {
          existing.start_date = campaign.start_date;
        }
        if (campaign.end_date > existing.end_date) {
          existing.end_date = campaign.end_date;
        }
      } else {
        const campaignCopy = { ...campaign };
        const campaignPlatforms = parseSocialMedia(campaign.social_media);
        (campaignCopy as any).groupedCampaigns = campaignPlatforms.map(platform => ({
          id: campaign.id,
          platform: platform,
          generated_content_id: campaign.generated_content_id
        }));
        groupMap.set(groupKey, campaignCopy);
      }
    });

    return Array.from(groupMap.values());
  };

  const getCampaignsForDate = (date: Date) => {
    const dateStr = formatDateForInput(date);
    const filteredCampaigns = campaigns.filter(campaign => {
      return dateStr >= campaign.start_date && dateStr <= campaign.end_date;
    });
    return groupCampaigns(filteredCampaigns);
  };

  const getMonthDates = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    const dates = [];
    const startDay = firstDay.getDay();

    for (let i = startDay - 1; i >= 0; i--) {
      const prevDate = new Date(firstDay);
      prevDate.setDate(prevDate.getDate() - (i + 1));
      dates.push({ date: prevDate, currentMonth: false });
    }

    for (let i = 1; i <= lastDay.getDate(); i++) {
      dates.push({ date: new Date(year, month, i), currentMonth: true });
    }

    const remainingDays = 42 - dates.length;
    for (let i = 1; i <= remainingDays; i++) {
      const nextDate = new Date(year, month + 1, i);
      dates.push({ date: nextDate, currentMonth: false });
    }

    return dates;
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    const newDate = new Date(selectedDate);
    newDate.setMonth(newDate.getMonth() + (direction === 'next' ? 1 : -1));
    setSelectedDate(newDate);
  };

  const navigateWeek = (direction: 'prev' | 'next') => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + (direction === 'next' ? 7 : -7));
    setSelectedDate(newDate);
  };

  const navigateDay = (direction: 'prev' | 'next') => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + (direction === 'next' ? 1 : -1));
    setSelectedDate(newDate);
  };

  const getWeekDates = (date: Date) => {
    const startOfWeek = new Date(date);
    const day = startOfWeek.getDay();
    startOfWeek.setDate(startOfWeek.getDate() - day);

    const weekDates = [];
    for (let i = 0; i < 7; i++) {
      const weekDate = new Date(startOfWeek);
      weekDate.setDate(weekDate.getDate() + i);
      weekDates.push(weekDate);
    }
    return weekDates;
  };

  const toggleCellExpansion = (dateKey: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedCells(prev => {
      const newSet = new Set(prev);
      if (newSet.has(dateKey)) {
        newSet.delete(dateKey);
      } else {
        newSet.add(dateKey);
      }
      return newSet;
    });
  };

  const getCellDateKey = (date: Date) => formatDateForInput(date);

  const parseContentPayload = (campaign: ScheduledCampaign) => {
    if (campaign.generated_content?.idea_title) {
      try {
        const payload = campaign.generated_content.content_payload
          ? JSON.parse(campaign.generated_content.content_payload)
          : {};
        return {
          title: campaign.generated_content.idea_title,
          description: payload.content_blocks?.body_copy || payload.target_purpose || '',
        };
      } catch {
        return {
          title: campaign.generated_content.idea_title,
          description: '',
        };
      }
    }

    try {
      if (campaign.generated_content?.content_payload) {
        const payload = JSON.parse(campaign.generated_content.content_payload);
        const title = payload.flyer_title ||
          payload.content_blocks?.headline ||
          `Campaign #${campaign.id}`;
        return {
          title,
          description: payload.content_blocks?.body_copy || payload.target_purpose || '',
        };
      }
    } catch (error) {
      console.error('Error parsing content_payload:', error);
    }
    return {
      title: `Campaign #${campaign.id}`,
      description: '',
    };
  };

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const renderWeekView = () => {
    const weekDates = getWeekDates(selectedDate);
    const weekDays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

    return (
      <div>
        <div className="grid grid-cols-7 gap-3">
          {weekDates.map((date, idx) => {
            const dayCampaigns = getCampaignsForDate(date);
            const isToday = date.toDateString() === new Date().toDateString();

            return (
              <div key={idx} className="flex flex-col">
                <div className={`text-center p-3 rounded-t-lg border-b-2 ${isToday
                    ? 'bg-[#616161] text-white'
                    : 'bg-[#616161] border-[#E0E0E040]'
                  }`}>
                  <div className="text-xs font-normal text-white">
                    {weekDays[idx].substring(0, 3)}
                  </div>
                  <div className="text-lg font-medium text-white">
                    {date.getDate()}
                  </div>
                </div>

                <div className="flex-1 border border-t-0 rounded-b-lg p-2 min-h-[70vh] bg-[#212121] border-[#E0E0E040]">
                  {dayCampaigns.length > 0 ? (
                    <div className="space-y-2">
                      {dayCampaigns.map((campaign) => {
                        const { title } = parseContentPayload(campaign);
                        const platforms = parseSocialMedia(campaign.social_media);

                        return (
                          <div
                            key={campaign.id}
                            onClick={(e) => handleEventClick(campaign, e)}
                            className="text-xs p-2 rounded border cursor-pointer transition-all bg-[#313131] border-[#E0E0E021] hover:border-[#E0E0E021] hover:bg-[#2f2f2f]"
                          >
                            <div className="flex items-start gap-1 mb-1">
                              {platforms.slice(0, 3).map((platform, idx) => (
                                <div key={idx}>
                                  {getSocialMediaIcon(platform)}
                                </div>
                              ))}
                              <span className="text-[10px] text-[#ffffff70]">
                                {formatTime(campaign.created_at)}
                              </span>
                            </div>
                            <p className="text-[10px] font-normal line-clamp-2 leading-tight text-white">
                              {title}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-full text-gray-400">
                      <div className="text-center">
                        <svg className="w-8 h-8 mx-auto mb-2 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <p className="text-xs">No campaigns</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderDayView = () => {
    const dayCampaigns = getCampaignsForDate(selectedDate);
    const isToday = selectedDate.toDateString() === new Date().toDateString();

    return (
      <div>
        <div className={`p-4 rounded-xl mb-3 ${isToday
            ? 'bg-[#616161] text-white'
            : 'bg-[#616161]'
          }`}>
          <div className="text-lg font-medium mb-1 text-white">
            {selectedDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            <p className="text-xs font-normal mb-1 text-white">
              {selectedDate.toLocaleDateString('en-US', { weekday: 'long' })}
            </p>
          </div>
          {dayCampaigns.length > 0 && (
            <div className="text-sm mt-2 text-white">
              {dayCampaigns.length} {dayCampaigns.length === 1 ? 'campaign' : 'campaigns'} scheduled
            </div>
          )}
        </div>

        {dayCampaigns.length > 0 ? (
          <div className="space-y-3">
            {dayCampaigns.map((campaign) => {
              const platforms = parseSocialMedia(campaign.social_media);
              const { title, description } = parseContentPayload(campaign);

              return (
                <div
                  key={campaign.id}
                  onClick={(e) => handleEventClick(campaign, e)}
                  className="p-5 rounded-xl border cursor-pointer hover:shadow-lg transition-all duration-300 hover:scale-[1.01] bg-[#212121] border-[#E0E0E040] hover:border-[#E0E0E021]"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-1">
                        <h3 className="font-bold text-lg text-white">{title}</h3>
                      </div>

                      <div className="space-y-2 text-sm">
                        <div className="flex items-center space-x-2">
                          <div className="flex gap-1">
                            {platforms.map((platform, idx) => (
                              <div key={idx}>
                                {getSocialMediaIcon(platform)}
                              </div>
                            ))}
                          </div>
                          <span className="text-xs text-gray-300">
                            {platforms.map(p => p.trim()).join(', ')}
                          </span>
                        </div>

                        <div className="flex items-center space-x-2 text-gray-300">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <span className="text-xs">
                            {formatDateForInput(new Date(campaign.start_date))} - {formatDateForInput(new Date(campaign.end_date))}
                          </span>
                        </div>
                      </div>
                    </div>
                    <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-16 rounded-xl bg-[#212121] border border-[#E0E0E040]">
            <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 bg-[#616161]">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <p className="font-medium text-lg text-white">No campaigns scheduled for this day</p>
            <p className="text-sm mt-2 mb-6 text-gray-400">Schedule a campaign to see it here</p>
          </div>
        )}
      </div>
    );
  };

  const renderMonthView = () => {
    const monthDates = getMonthDates(selectedDate);
    const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    return (
      <div>
        <div className="grid grid-cols-7 gap-2 mb-2">
          {weekDays.map((day) => (
            <div key={day} className="text-center text-sm  font-normal py-3 text-[#fff] bg-[#616161] rounded-t-lg">
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-2">
          {monthDates.map(({ date, currentMonth }, idx) => {
            const dayCampaigns = getCampaignsForDate(date);
            const hasCampaigns = dayCampaigns.length > 0;
            const isToday = date.toDateString() === new Date().toDateString();

            return (
              <div
                key={idx}
                className={`min-h-[120px] p-2 rounded-lg border transition-all duration-200 ${!currentMonth
                    ? 'bg-[#212121]/20 border-[#E0E0E021] opacity-20'
                    : hasCampaigns
                      ? 'bg-[#212121] border-[#E0E0E040] cursor-pointer'
                      : isToday
                        ? 'bg-[#313131] border-blue-500/30'
                        : 'bg-[#212121] border-[#E0E0E040]'
                  }`}
              >
                <div className={`text-sm font-normal mb-2 ${hasCampaigns ? 'text-gray-300' : 'text-gray-500'
                  }`}>
                  {date.getDate()}
                </div>
                {hasCampaigns && (
                  <div className="space-y-1">
                    {dayCampaigns.slice(0, 2).map((campaign) => {
                      const { title } = parseContentPayload(campaign);
                      const platforms = parseSocialMedia(campaign.social_media);

                      return (
                        <div
                          key={campaign.id}
                          onClick={(e) => handleEventClick(campaign, e)}
                          className="text-xs p-1.5 rounded border cursor-pointer transition-all bg-[#313131] border-[#E0E0E021] hover:border-[#E0E0E021] hover:bg-[#2f2f2f]"
                        >
                          <div className="flex items-start gap-1 mb-1">
                            {platforms.slice(0, 2).map((platform, idx) => (
                              <div key={idx} className="flex-shrink-0">
                                {getSocialMediaIcon(platform)}
                              </div>
                            ))}
                            <span className="text-[10px] text-[#ffffff70]">
                              {formatTime(campaign.created_at)}
                            </span>
                          </div>
                          <p className="text-[10px] font-normal line-clamp-2 leading-tight text-white">
                            {title}
                          </p>
                        </div>
                      );
                    })}

                    {dayCampaigns.length > 2 && (
                      <div
                        className={`overflow-hidden transition-all duration-300 ease-in-out space-y-1 ${expandedCells.has(getCellDateKey(date)) ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                          }`}
                      >
                        {dayCampaigns.slice(2).map((campaign) => {
                          const { title } = parseContentPayload(campaign);
                          const platforms = parseSocialMedia(campaign.social_media);

                          return (
                            <div
                              key={campaign.id}
                              onClick={(e) => handleEventClick(campaign, e)}
                              className="text-xs p-1.5 rounded border cursor-pointer transition-all bg-[#313131] border-[#212121] hover:border-[#4a4a4a] hover:bg-[#2f2f2f]"
                            >
                              <div className="flex items-start gap-1 mb-1">
                                {platforms.slice(0, 2).map((platform, idx) => (
                                  <div key={idx} className="flex-shrink-0">
                                    {getSocialMediaIcon(platform)}
                                  </div>
                                ))}
                                <span className="text-[10px] text-[#ffffff70]">
                                  {formatTime(campaign.created_at)}
                                </span>
                              </div>
                              <p className="text-[10px] font-normal line-clamp-2 leading-tight text-white">
                                {title}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {dayCampaigns.length > 2 && (
                      <button
                        onClick={(e) => toggleCellExpansion(getCellDateKey(date), e)}
                        className="text-xs pl-1 mt-1 hover:underline cursor-pointer font-medium transition-colors duration-50 text-gray-400 hover:text-gray-200"
                      >
                        {expandedCells.has(getCellDateKey(date)) ? 'Show less' : `+${dayCampaigns.length - 2} more`}
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (loading) {
    return <LoadingState message="Loading campaigns..." />;
  }

  return (
    <div className="min-h-screen">
      <div className="max-w-full mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#EE4423] shadow-lg">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Scheduled Campaigns</h2>
              <p className="text-xs text-gray-400">View and manage your scheduled campaigns</p>
            </div>
          </div>

          {/* View Controls */}
          <div className="flex items-center space-x-3">
            <div className="flex items-center rounded-lg px-1 py-[2px] bg-[#212121] border border-gray-700">
              <button
                onClick={() => {
                  if (viewMode === 'month') navigateMonth('prev');
                  else if (viewMode === 'week') navigateWeek('prev');
                  else navigateDay('prev');
                }}
                className="p-1.5 rounded-md cursor-pointer transition-colors hover:bg-orange-900/30"
              >
                <svg className="w-4 h-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              <button
                onClick={() => setSelectedDate(new Date())}
                className="px-3 py-1 rounded-md transition-all text-[11px] font-medium mx-1 bg-[#EE4423] text-white shadow-sm"
              >
                {selectedDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}
              </button>

              <button
                onClick={() => {
                  if (viewMode === 'month') navigateMonth('next');
                  else if (viewMode === 'week') navigateWeek('next');
                  else navigateDay('next');
                }}
                className="p-1.5 rounded-md cursor-pointer transition-colors hover:bg-orange-900/30"
              >
                <svg className="w-4 h-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            {/* View Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowViewDropdown(!showViewDropdown)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium bg-[#212121] text-gray-300 border border-gray-700 hover:border-gray-600 transition-all min-w-[100px] justify-between"
              >
                <span className="capitalize">{viewMode}</span>
                <svg className={`w-4 h-4 transition-transform ${showViewDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {showViewDropdown && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowViewDropdown(false)}
                  />
                  <div className="absolute right-0 mt-2 w-32 rounded-lg shadow-lg bg-[#2a2a2a] border border-gray-700 overflow-hidden z-20">
                    {(['month', 'week', 'day'] as ViewMode[]).map((mode) => (
                      <button
                        key={mode}
                        onClick={() => {
                          setViewMode(mode);
                          setShowViewDropdown(false);
                        }}
                        className={`w-full px-4 py-2 text-left text-xs font-medium capitalize transition-colors ${viewMode === mode
                            ? 'bg-[#EE4423] text-white'
                            : 'text-gray-300 hover:bg-[#3a3a3a]'
                          }`}
                      >
                        {mode}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Calendar content */}
        <div
          className="rounded-lg p-4 shadow-sm border"
          style={{
            backdropFilter: 'blur(10px)',
            boxShadow: 'rgba(0, 0, 0, 0.1) 0px 0px 14px 0px',
            background: 'linear-gradient(90deg, rgba(33, 33, 33, 0.92), rgba(55, 55, 55, 0.92))',
            border: '1px solid rgba(224, 224, 224, 0.13)'
          }}
        >
          {viewMode === 'month' && renderMonthView()}
          {viewMode === 'week' && renderWeekView()}
          {viewMode === 'day' && renderDayView()}
        </div>

        <CampaignDetailModal
          campaign={selectedCampaign}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          clickPosition={clickPosition}
        />
      </div>
    </div>
  );
};

export default MarketingCalendar;
